import {
  Alert,
  Button,
  Checkbox,
  DEFAULT_DIAL_CODE,
  Field,
  IconBadge,
  Input,
  PhoneInput,
  SegmentedControl,
  Select,
  type SegmentedOption,
} from '@fidalia/ui';
import { useRegister, useRegisterWithGoogle } from '@fidalia/api';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { MdArrowBack, MdBusiness, MdMail, MdMarkEmailUnread, MdPerson } from 'react-icons/md';
import { useState } from 'react';
import { z } from 'zod';

import { AuthLayout } from '@/features/auth/auth-layout';
import { OtpStep } from '@/features/auth/otp-step';
import { track } from '@/lib/analytics';
import { errorMessage, OTP_LENGTH } from '@/lib/auth';
import { clearFieldErrors, collectIssues, fieldError, useAttemptErrors } from '@/lib/form';
import { siteLinks } from '@/lib/links';

const EXERCICES = [
  {
    value: 'cabinet',
    label: 'En cabinet',
    hint: 'Plusieurs praticiens',
    icon: <MdBusiness className="size-6" />,
  },
  {
    value: 'seul',
    label: 'Seul(e)',
    hint: 'Praticien indépendant',
    icon: <MdPerson className="size-6" />,
  },
] as const satisfies readonly SegmentedOption<'cabinet' | 'seul'>[];

const FONCTIONS = [
  { value: 'praticien', label: 'Praticien / chirurgien-dentiste' },
  { value: 'assistant', label: 'Assistant(e) dentaire' },
  { value: 'secretariat', label: 'Secrétariat' },
  { value: 'gestionnaire', label: 'Gestionnaire du cabinet' },
] as const;

const FONCTION_VALUES = ['praticien', 'assistant', 'secretariat', 'gestionnaire'] as const;
const EXERCICE_VALUES = ['cabinet', 'seul'] as const;
const PHONE_PATTERN = /^\d[\d\s().-]{5,}$/;

const emailFormat = z.string().trim().email('Adresse email invalide.');

/**
 * Schéma de saisie : ne juge que le format. Un champ vide passe, l'erreur
 * « champ obligatoire » n'est produite qu'au clic sur Continuer / Terminer.
 */
const draftSchema = z.object({
  prenom: z.string().refine((v) => v === '' || v.trim().length >= 2, 'Indiquez votre prénom.'),
  nom: z.string().refine((v) => v === '' || v.trim().length >= 2, 'Indiquez votre nom.'),
  fonction: z.enum(FONCTION_VALUES),
  exercice: z.enum(EXERCICE_VALUES),
  email: z.string().refine((v) => v === '' || emailFormat.safeParse(v).success, {
    message: 'Adresse email invalide.',
  }),
  indicatif: z.string(),
  telephone: z.string().refine((v) => v === '' || PHONE_PATTERN.test(v.trim()), {
    message: 'Numéro de téléphone invalide.',
  }),
  cgu: z.boolean(),
});

/** Schéma complet, appliqué à la validation d'une étape. */
const registerSchema = z.object({
  prenom: z.string().trim().min(2, 'Indiquez votre prénom.'),
  nom: z.string().trim().min(2, 'Indiquez votre nom.'),
  fonction: z.enum(FONCTION_VALUES, { message: 'Choisissez votre fonction.' }),
  exercice: z.enum(EXERCICE_VALUES),
  email: z.string().trim().min(1, 'Renseignez votre email.').pipe(emailFormat),
  indicatif: z.string().min(2),
  telephone: z
    .string()
    .trim()
    .min(1, 'Renseignez un numéro de téléphone.')
    .regex(PHONE_PATTERN, 'Numéro de téléphone invalide.'),
  cgu: z.literal(true, { message: 'Vous devez accepter les conditions.' }),
});

const stepOneSchema = registerSchema.pick({
  prenom: true,
  nom: true,
  fonction: true,
  exercice: true,
});

const STEPS = [
  { title: 'Créer un compte', subtitle: 'Renseignez vos informations.' },
  { title: 'Vos coordonnées', subtitle: 'Renseignez vos coordonnées.' },
] as const;

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const attempt = useAttemptErrors();
  const register = useRegister();
  const registerWithGoogle = useRegisterWithGoogle();

  /**
   * Ticket délivré par le retour OAuth quand l'adresse Google est inconnue.
   * Sa présence bascule le formulaire en « inscription Google » : l'email est
   * imposé par le ticket, et aucun code de vérification n'est envoyé.
   */
  const ticketGoogle = useRouterState({ select: (state) => state.location.state.ticketGoogle });
  const viaGoogle = Boolean(ticketGoogle);

  // Affiché en lecture seule. C'est un simple confort : le backend ne lit
  // jamais cette valeur, il reprend l'email dans le ticket signé.
  const emailGoogle = ticketGoogle ? emailDuTicket(ticketGoogle) : null;
  const identiteGoogle = ticketGoogle ? identiteDuTicket(ticketGoogle) : null;

  const form = useForm({
    defaultValues: {
      prenom: identiteGoogle?.prenom ?? '',
      nom: identiteGoogle?.nom ?? '',
      fonction: 'praticien' as (typeof FONCTION_VALUES)[number],
      exercice: 'cabinet' as (typeof EXERCICE_VALUES)[number],
      email: emailGoogle ?? '',
      indicatif: DEFAULT_DIAL_CODE,
      telephone: '',
      cgu: false,
    },
    // Validation au blur uniquement : on ne corrige pas l'utilisateur pendant
    // qu'il tape. Les erreurs sont effacées à la frappe (`clearFieldErrors`).
    validators: { onBlur: draftSchema },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const email = value.email.trim();

      const commun = {
        prenom: value.prenom.trim(),
        nom: value.nom.trim(),
        indicatif: value.indicatif,
        telephone: value.telephone.trim(),
        fonction: value.fonction,
        exercice: value.exercice,
        cguAccepted: true as const,
      };

      try {
        if (ticketGoogle) {
          // Google a déjà prouvé l'adresse : le compte est créé et la session
          // ouverte d'un coup, sans code à saisir.
          await registerWithGoogle.mutateAsync({ ...commun, ticket: ticketGoogle });
          track('signup_completed', {
            method: 'google',
            practice_type: value.exercice,
            job: value.fonction,
          });
          await navigate({ to: '/', replace: true });
          return;
        }

        // Sinon l'inscription crée le cabinet et envoie le code : la session
        // ne s'ouvre qu'à l'étape suivante, une fois le code vérifié.
        await register.mutateAsync({ ...commun, email });
        track('signup_submitted', {
          method: 'email',
          practice_type: value.exercice,
          job: value.fonction,
        });
        setSentTo(email);
      } catch (error) {
        setFormError(errorMessage(error, 'Inscription impossible.'));
      }
    },
  });

  const goToStepTwo = () => {
    setFormError(null);
    const result = stepOneSchema.safeParse(form.state.values);
    if (!result.success) {
      attempt.setErrors(collectIssues(result.error));
      return;
    }
    attempt.reset();
    setStep(1);
  };

  const submit = async () => {
    setFormError(null);
    const result = registerSchema.safeParse(form.state.values);
    if (!result.success) {
      attempt.setErrors(collectIssues(result.error));
      return;
    }
    attempt.reset();
    await form.handleSubmit();
  };

  const backToStepOne = () => {
    attempt.reset();
    setStep(0);
  };

  if (sentTo) {
    return (
      <AuthLayout
        title="Vérifier votre email"
        subtitle={
          <>
            Veuillez saisir le code à {OTP_LENGTH} chiffres envoyé à{' '}
            <span className="font-medium text-content">{sentTo}</span>.
          </>
        }
        icon={
          <IconBadge size="2xl">
            <MdMarkEmailUnread />
          </IconBadge>
        }
      >
        <OtpStep
          email={sentTo}
          onBack={() => {
            setSentTo(null);
            backToStepOne();
          }}
          onVerified={() => navigate({ to: '/' })}
          submitLabel="Confirmer"
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={(STEPS[step] ?? STEPS[0]).title}
      subtitle={(STEPS[step] ?? STEPS[0]).subtitle}
      footer={
        <>
          Déjà un compte ?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:underline">
            Se connecter
          </Link>
        </>
      }
    >
      {viaGoogle ? (
        <Alert tone="success">
          Adresse <span className="font-medium">{emailGoogle}</span> vérifiée par Google. Complétez
          votre profil pour terminer.
        </Alert>
      ) : null}

      {formError ? <Alert tone="danger">{formError}</Alert> : null}

      <form
        noValidate
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {step === 0 ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="prenom">
                {(field) => {
                  const error = fieldError(field, attempt.errors);
                  return (
                    <Field label="Prénom" htmlFor={field.name} error={error}>
                      <Input
                        id={field.name}
                        name={field.name}
                        autoComplete="given-name"
                        autoFocus
                        placeholder="John"
                        icon={<MdPerson className="size-6" />}
                        invalid={Boolean(error)}
                        value={field.state.value}
                        onChange={(event) => {
                          attempt.clear(field.name);
                          clearFieldErrors(field);
                          field.handleChange(event.target.value);
                        }}
                        onBlur={field.handleBlur}
                      />
                    </Field>
                  );
                }}
              </form.Field>

              <form.Field name="nom">
                {(field) => {
                  const error = fieldError(field, attempt.errors);
                  return (
                    <Field label="Nom" htmlFor={field.name} error={error}>
                      <Input
                        id={field.name}
                        name={field.name}
                        autoComplete="family-name"
                        placeholder="Doe"
                        invalid={Boolean(error)}
                        value={field.state.value}
                        onChange={(event) => {
                          attempt.clear(field.name);
                          clearFieldErrors(field);
                          field.handleChange(event.target.value);
                        }}
                        onBlur={field.handleBlur}
                      />
                    </Field>
                  );
                }}
              </form.Field>
            </div>

            <form.Field name="fonction">
              {(field) => {
                const error = fieldError(field, attempt.errors);
                return (
                  <Field label="Votre fonction" htmlFor={field.name} error={error}>
                    <Select
                      id={field.name}
                      name={field.name}
                      invalid={Boolean(error)}
                      value={field.state.value}
                      onChange={(event) => {
                        attempt.clear(field.name);
                        clearFieldErrors(field);
                        field.handleChange(event.target.value as typeof field.state.value);
                      }}
                      onBlur={field.handleBlur}
                    >
                      {FONCTIONS.map((fonction) => (
                        <option key={fonction.value} value={fonction.value}>
                          {fonction.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="exercice">
              {(field) => (
                <Field label="Vous exercez" htmlFor={field.name}>
                  <SegmentedControl
                    name={field.name}
                    value={field.state.value}
                    onChange={field.handleChange}
                    options={EXERCICES}
                    layout="grid"
                  />
                </Field>
              )}
            </form.Field>

            <Button type="button" size="lg" fullWidth onClick={goToStepTwo}>
              Continuer
            </Button>
          </>
        ) : (
          <>
            <form.Field name="email">
              {(field) => {
                const error = fieldError(field, attempt.errors);
                return (
                  <Field
                    label="Email"
                    htmlFor={field.name}
                    error={error}
                    hint={viaGoogle ? 'Fournie par Google, non modifiable.' : undefined}
                  >
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      autoComplete="email"
                      autoFocus={!viaGoogle}
                      placeholder="john.doe@exemple.fr"
                      icon={<MdMail className="size-6" />}
                      invalid={Boolean(error)}
                      // Verrouillé côté écran par cohérence seulement : la
                      // valeur envoyée n'est de toute façon jamais lue, le
                      // backend reprend l'email dans le ticket signé.
                      readOnly={viaGoogle}
                      value={field.state.value}
                      onChange={(event) => {
                        if (viaGoogle) return;
                        attempt.clear(field.name);
                        clearFieldErrors(field);
                        field.handleChange(event.target.value);
                      }}
                      onBlur={field.handleBlur}
                    />
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="telephone">
              {(field) => {
                const error = fieldError(field, attempt.errors);
                return (
                  <Field label="Numéro de téléphone" htmlFor={field.name} error={error}>
                    <form.Field name="indicatif">
                      {(indicatif) => (
                        <PhoneInput
                          id={field.name}
                          name={field.name}
                          autoComplete="tel-national"
                          placeholder="6 12 34 56 78"
                          invalid={Boolean(error)}
                          dialCode={indicatif.state.value}
                          onDialCodeChange={indicatif.handleChange}
                          onDialCodeBlur={indicatif.handleBlur}
                          value={field.state.value}
                          onChange={(event) => {
                            attempt.clear(field.name);
                            field.handleChange(event.target.value);
                          }}
                          onBlur={field.handleBlur}
                        />
                      )}
                    </form.Field>
                  </Field>
                );
              }}
            </form.Field>

            <div className="flex flex-col gap-4">
              <form.Field name="cgu">
                {(field) => {
                  const error = fieldError(field, attempt.errors);
                  return (
                    <div className="flex flex-col gap-2">
                      <Checkbox
                        name={field.name}
                        checked={field.state.value}
                        onChange={(event) => {
                          attempt.clear(field.name);
                          clearFieldErrors(field);
                          field.handleChange(event.target.checked);
                        }}
                        onBlur={field.handleBlur}
                        label={
                          <span>
                            J’accepte les{' '}
                            <a
                              href={siteLinks.cgu}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-brand-600 hover:underline"
                            >
                              conditions d’utilisation
                            </a>{' '}
                            et la{' '}
                            <a
                              href={siteLinks.confidentialite}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-brand-600 hover:underline"
                            >
                              politique de confidentialité
                            </a>
                            .
                          </span>
                        }
                      />
                      {error ? (
                        <p role="alert" className="text-sm text-danger">
                          {error}
                        </p>
                      ) : null}
                    </div>
                  );
                }}
              </form.Field>

              <form.Subscribe selector={(state) => state.isSubmitting}>
                {(isSubmitting) => (
                  <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
                    {isSubmitting ? 'Envoi…' : 'Terminer'}
                  </Button>
                )}
              </form.Subscribe>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={backToStepOne}
                icon={<MdArrowBack className="size-4" />}
                className="self-center"
              >
                Retour
              </Button>
            </div>
          </>
        )}
      </form>
    </AuthLayout>
  );
}

/**
 * Décode la charge du ticket **sans vérifier sa signature**, c'est
 * volontaire : ces valeurs ne servent qu'à préremplir l'affichage. Le backend
 * revérifie le ticket et n'utilise que l'email qu'il contient.
 */
function chargeDuTicket(ticket: string): Record<string, unknown> | null {
  try {
    const charge = ticket.split('.')[1];
    if (!charge) return null;
    return JSON.parse(atob(charge.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

function emailDuTicket(ticket: string): string | null {
  const email = chargeDuTicket(ticket)?.email;
  return typeof email === 'string' ? email : null;
}

function identiteDuTicket(ticket: string): { prenom: string; nom: string } {
  const charge = chargeDuTicket(ticket);
  return {
    prenom: typeof charge?.prenom === 'string' ? charge.prenom : '',
    nom: typeof charge?.nom === 'string' ? charge.nom : '',
  };
}
